import { AddIcon } from '@approvalmax/ui';
import { Box, Button, Form, Grid, Text, TextField } from '@approvalmax/ui/src/components';
import { domHelpers } from '@approvalmax/utils';
import { constants } from 'modules/common';
import { memo, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';

import { EditPhoneInput } from '../../../../../components/EditPhoneInput/EditPhoneInput';
import { schema } from '../../XeroContactRequestForm.schema';
import { messages } from './ContactInformationPart.messages';

const qa = 'contact-information';

export const ContactInformationPart = memo(() => {
    const form = useFormContext<z.infer<typeof schema>>();

    const persons = form.watch('persons');

    const addPerson = useCallback(() => {
        form.setValue('persons', [
            ...(form.getValues().persons ?? []),
            { firstName: '', lastName: '', emailAddress: '' },
        ]);
    }, [form]);

    const removePerson = useCallback(
        (index: number) => {
            form.setValue(
                'persons',
                (form.getValues().persons ?? []).filter((_person, personIndex) => personIndex !== index)
            );
        },
        [form]
    );

    return (
        <Form.Part>
            <div>
                <Box spacing='16 0'>
                    <Text color='midnight80' font='headline' fontSize='xxsmall' fontWeight='medium'>
                        {messages.title}
                    </Text>
                </Box>

                <Grid gridTemplateColumns='1fr 1fr' columnGap={16} rowGap={16}>
                    <TextField
                        name='name'
                        label={messages.contactName}
                        required
                        size='medium'
                        placeholder={messages.contactNamePlaceholder}
                        qa={domHelpers.generateDataQa(qa, 'name-editor')}
                    />

                    <TextField
                        name='accountNumber'
                        label={messages.accountNumber}
                        size='medium'
                        placeholder={messages.accountNumberPlaceholder}
                        qa={domHelpers.generateDataQa(qa, 'account-number-editor')}
                    />

                    <TextField
                        name='primaryPersonFirstName'
                        label={messages.primaryPersonFirstName}
                        size='medium'
                        placeholder={messages.primaryPersonFirstNamePlaceholder}
                        qa={domHelpers.generateDataQa(qa, 'primary-first-name-editor')}
                    />

                    <TextField
                        name='primaryPersonLastName'
                        label={messages.primaryPersonLastName}
                        size='medium'
                        placeholder={messages.primaryPersonLastNamePlaceholder}
                        qa={domHelpers.generateDataQa(qa, 'primary-last-name-editor')}
                    />

                    <TextField
                        name='primaryPersonEmail'
                        label={messages.email}
                        size='medium'
                        placeholder={messages.emailPlaceholder}
                        qa={domHelpers.generateDataQa(qa, 'primary-email-editor')}
                    />
                </Grid>

                {Boolean(persons?.length) && (
                    <Grid rowGap={24} padding='24 0 0 0'>
                        {persons?.map((_person, index) => (
                            <Grid key={index} rowGap={8}>
                                <Grid alignItems='center' gridTemplateColumns='auto max-content' padding='8 0'>
                                    <Text font='label' fontSize='medium' fontWeight='medium'>
                                        {messages.anotherPersonTitle({ index: index + 1 })}
                                    </Text>

                                    <Button
                                        color='red40'
                                        variant='outline'
                                        size='xsmall'
                                        onClick={() => removePerson(index)}
                                    >
                                        {messages.remove}
                                    </Button>
                                </Grid>

                                <Grid gridTemplateColumns='1fr 1fr 1fr' columnGap={16}>
                                    <TextField
                                        name={`persons.${index}.firstName`}
                                        label={messages.firstName}
                                        required
                                        size='medium'
                                        placeholder={messages.firstNamePlaceholder}
                                        qa={domHelpers.generateDataQa(qa, 'person-first-name-editor')}
                                    />

                                    <TextField
                                        name={`persons.${index}.lastName`}
                                        label={messages.lastName}
                                        size='medium'
                                        placeholder={messages.lastNamePlaceholder}
                                        qa={domHelpers.generateDataQa(qa, 'person-last-name-editor')}
                                    />

                                    <TextField
                                        name={`persons.${index}.emailAddress`}
                                        label={messages.email}
                                        size='medium'
                                        placeholder={messages.emailPlaceholder}
                                        qa={domHelpers.generateDataQa(qa, 'person-email-editor')}
                                    />
                                </Grid>
                            </Grid>
                        ))}
                    </Grid>
                )}

                <Box spacing='8 0 24 0'>
                    <Button color='blue10' size='small' startIcon={<AddIcon />} onClick={addPerson}>
                        {messages.addAnotherPerson}
                    </Button>
                </Box>

                <Grid gridTemplateColumns='1fr 1fr' gap={16}>
                    <EditPhoneInput
                        name='phone'
                        label={messages.phoneLabel}
                        qa={domHelpers.generateDataQa(qa, 'phone-editor')}
                    />

                    <EditPhoneInput
                        name='fax'
                        label={messages.faxLabel}
                        qa={domHelpers.generateDataQa(qa, 'fax-editor')}
                    />

                    <EditPhoneInput
                        name='mobile'
                        label={messages.mobileLabel}
                        qa={domHelpers.generateDataQa(qa, 'mobile-editor')}
                    />

                    <EditPhoneInput
                        name='directDial'
                        label={messages.directDialLabel}
                        qa={domHelpers.generateDataQa(qa, 'direct-dial-editor')}
                    />

                    <TextField
                        name='skypeName'
                        label={messages.skypeNameLabel}
                        placeholder={messages.skypeNamePlaceholder}
                        maxLength={constants.xeroConstants.SKYPE_MAX_LENGTH}
                        qa={domHelpers.generateDataQa(qa, 'skype-editor')}
                    />
                </Grid>
            </div>
        </Form.Part>
    );
});

ContactInformationPart.displayName = 'ContactInformationPart';
